import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ChevronDown, LogOut, ListVideo, User as UserIcon, Menu, X, Settings, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Browse", to: "/browse" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/browse?q=${encodeURIComponent(query.trim())}`);
  };

  const hidden = location.pathname.startsWith("/watch");
  if (hidden) return null;

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-white/5" : "bg-gradient-to-b from-black/80 to-transparent"
      }`}
    >
      <div className="px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Link to="/" data-testid="logo-link" className="shrink-0">
            <Logo size="md" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:text-white ${
                  location.pathname === l.to ? "text-white" : "text-white/60"
                }`}
              >
                {l.label}
              </Link>
            ))}
            {user && (
              <Link to="/my-list" data-testid="nav-mylist" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
                My List
              </Link>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <form onSubmit={submitSearch} className="hidden sm:flex items-center bg-surface/80 border border-white/10 rounded-full px-3 py-1.5 focus-within:ring-1 focus-within:ring-gold">
            <Search className="w-4 h-4 text-white/50" />
            <input
              data-testid="navbar-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search titles, actors..."
              className="bg-transparent outline-none text-sm px-2 w-32 lg:w-52 placeholder:text-white/40"
            />
          </form>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="flex items-center gap-2 outline-none">
                <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-gold to-gold-bright text-ink flex items-center justify-center font-bold text-sm uppercase">
                  {(user.name || "U").charAt(0)}
                </div>
                <ChevronDown className="w-4 h-4 text-white/60 hidden sm:block" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-surface border-white/10 text-white w-52">
                <div className="px-2 py-1.5 text-xs text-white/50 truncate">{user.email}</div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")} className="cursor-pointer focus:bg-white/10">
                  <UserIcon className="w-4 h-4 mr-2" /> Account & Profiles
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-mylist" onClick={() => navigate("/my-list")} className="cursor-pointer focus:bg-white/10">
                  <ListVideo className="w-4 h-4 mr-2" /> My List
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-purchases" onClick={() => navigate("/account")} className="cursor-pointer focus:bg-white/10">
                  <ShoppingBag className="w-4 h-4 mr-2 text-gold" /> My Purchases
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-white/10">
                    <Settings className="w-4 h-4 mr-2 text-gold" /> Admin Dashboard
                  </DropdownMenuItem>
                )}
                {user.role === "distributor" && (
                  <DropdownMenuItem data-testid="menu-distributor" onClick={() => navigate("/distributor")} className="cursor-pointer focus:bg-white/10">
                    <Settings className="w-4 h-4 mr-2 text-gold" /> Distributor Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }} className="cursor-pointer focus:bg-white/10">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/login"
              data-testid="navbar-signin-btn"
              className="bg-gold hover:bg-gold-bright transition-colors text-ink text-sm font-medium px-5 py-2 rounded-sm"
            >
              Sign In
            </Link>
          )}

          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((o) => !o)} className="md:hidden text-white">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-ink/95 backdrop-blur-xl border-t border-white/5 px-4 py-4 flex flex-col gap-3">
          <form onSubmit={submitSearch} className="flex items-center bg-surface border border-white/10 rounded-full px-3 py-2 mb-1">
            <Search className="w-4 h-4 text-white/50" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." className="bg-transparent outline-none text-sm px-2 flex-1" />
          </form>
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="text-white/80 py-1">
              {l.label}
            </Link>
          ))}
          {user && <Link to="/my-list" onClick={() => setMobileOpen(false)} className="text-white/80 py-1">My List</Link>}
        </div>
      )}
    </nav>
  );
}
